import { produce } from 'immer';

import { updateBaseSetting } from '@/api/streaming/streaming';
import { initStore } from '@/lib/store';
import type { StoreWithSelectors } from '@/lib/store';
import {
    Announcement,
    AnnouncementPinnedState,
    BeautyParamsDraft,
    ChatMessage,
    CommandRecordType,
    CommandType,
    FilterStatusType,
    GiftMessageWithType,
    RoomChangeState,
    SettingInfoType,
    StreamerInfo,
    StreamingInfo,
    StreamingType,
    UpdateSettingInfoType
} from '@/types/streaming';
import { ChatGiftItem } from '@/types/chat';
import { sampleGiftMsg } from '@/features/streaming/streamingRoom/chatroom/config';

export const initalFilter = {
    beauty_settings: {
        eye_bag: 0.84,
        nasolabial_fold: 0.78,
        white_teeth: 0.72,
        blush: 0.1,
        lipstick: 0,
        lipstick_color: 0.1,
        lipstick_gloss: 0.2,
        lipstick_brightness: 0.62,
        rosiness: 0.15,
        bright_eyes: 0.89,
        wrinkle_removal: 0.1,
        dark_circle_removal: 0.4
    },
    face_shaping: {
        cheekbone: 0,
        face_slimming: 0,
        face_thinning: 0.1,
        face_length: 0,
        chin_length: 0,
        chin_slimming: 0.1,
        jaw_slimming: 0,
        eye_enlargement: 0.1,
        eye_corner1: 0,
        eye_distance: 0,
        eye_distance_widening: 0,
        eye_corner2: 0,
        eye_height: 0,
        nose_slimming: 0.1,
        nose_wing: 0,
        nose_length: 0,
        nose_tip_length: 0,
        lip_width: 0,
        lip_size: -0.3,
        lip_height: 0,
        philtrum: 0,
        hairline: 0.1,
        smile: 0.2
    },
    body_shaping: {
        overall: 0,
        long_legs: 0,
        small_head: 0,
        slim_legs: 0,
        neck: 0,
        slim_waist: 0,
        bust_enhancement: 0,
        arms: 0
    }
};
interface BaseState {
    openStreamingPrepare: boolean;
    switchStreamingPrepare: (status: boolean) => void;
    streamingSettingInfo: SettingInfoType;
    setStreamingSettingInfo: (info: SettingInfoType) => void;
    setStreamingSettingInfoByKey: (
        target: keyof SettingInfoType,
        value: SettingInfoType[keyof SettingInfoType]
    ) => void;
    commandList: CommandType[];
    setCommandList: (commandList: CommandType[]) => void;
    streamingCommandRecordList: CommandRecordType[];
    setStreamingCommandRecordList: (action: "push" | "unshift" | "replace" | "clear", data?: CommandRecordType | CommandRecordType[]) => void;
    setFilterConfig: (updater: (draft: BeautyParamsDraft) => void) => void;
    updateFilterStatus: (status: FilterStatusType) => void;
    resetFilter: (filter: BeautyParamsDraft) => void;
    updateBaseInfo: (
        target?: keyof SettingInfoType,
        value?: SettingInfoType[keyof SettingInfoType]
    ) => Promise<void>;
    streamingInfo: StreamingInfo;
    setStreamingInfo: (streamingInfo: Partial<StreamingInfo>) => void;
    resetStreamingInfo: () => void;
    tempFilter: BeautyParamsDraft;
    streamerInfo: StreamerInfo;
    setStreamerInfo: (streamerInfo: Partial<StreamerInfo>) => void;
    resetStreamerInfo: () => void;
    setTempFilter: (updater: (draft: BeautyParamsDraft) => void) => void;
    chatMessages: ChatMessage[];
    setChatMessages: (newMessage: ChatMessage) => void;
    resetChatMessages: () => void;
    receivedGiftMsg: GiftMessageWithType[];
    setReceivedGiftMsg: (action: "push" | "unshift", msg?: GiftMessageWithType) => void;
    resetReceivedGiftMsg: () => void;
    receivedCommand: Set<string>;
    setReceivedCommand: (commandId: string) => void;
    resetReceivedCommand: () => void;
    usersInRoom: Set<string>;
    setUsersInRoom: (userId: string) => void;
    resetUsersInRoom: () => void;
    announcement: Announcement;
    setAnnouncement: (payload: Partial<Announcement>) => void;
    roomChangeState: RoomChangeState;
    setRoomChangeState: (state: RoomChangeState) => void;
    totalIncome: number;
    setTotalIncome: (diamond: number) => void;
}

let useStreamingStore: StoreWithSelectors<BaseState>;

const initialState = (set: (updater: (state: BaseState) => Partial<BaseState>) => void) => ({
    openStreamingPrepare: false,
    switchStreamingPrepare: (status: boolean) => {
        set(() => ({
            openStreamingPrepare: status
        }));
    },
    streamingSettingInfo: {
        id: '',
        userId: 0,
        title: '',
        labels: [],
        cover: '',
        type: 1 as StreamingType,
        diamond: 0,
        filter_status: 1 as FilterStatusType,
        filter_settings: initalFilter
    },
    setStreamingSettingInfo: (info: SettingInfoType) => {
        set(() => ({
            streamingSettingInfo: info
        }));
    },
    setStreamingSettingInfoByKey: (
        target: keyof SettingInfoType,
        value: SettingInfoType[keyof SettingInfoType]
    ) => {
        set(state => ({
            streamingSettingInfo: {
                ...state.streamingSettingInfo,
                [target]: value
            }
        }));
    },
    commandList: [],
    setCommandList: (commandList: CommandType[]) => {
        set(() => ({
            commandList
        }));
    },
    streamingCommandRecordList: [],
    setStreamingCommandRecordList: (action: "push" | "unshift" | "replace" | "clear", data?: CommandRecordType | CommandRecordType[]) => {
        set((state) => {
            if (action === "clear") {
                return { streamingCommandRecordList: [] }
            }
            if (action === "replace") {
                return { streamingCommandRecordList: data as CommandRecordType[] }
            }
            if (action === "push" && data) {
                return {
                    streamingCommandRecordList: [
                        ...state.streamingCommandRecordList,
                        data as CommandRecordType
                    ]
                }
            }
            return {
                streamingCommandRecordList: state.streamingCommandRecordList.filter((_item, i) => i !== 0)
            }
        });
    },
    updateFilterStatus: (status: FilterStatusType) => {
        set(state => ({
            streamingSettingInfo: {
                ...state.streamingSettingInfo,
                filter_status: status
            }
        }));
    },
    setFilterConfig: (updater: (draft: BeautyParamsDraft) => void) =>
        set(state => ({
            streamingSettingInfo: {
                ...state.streamingSettingInfo,
                filter_settings: produce(state.streamingSettingInfo.filter_settings, updater)
            }
        })),
    resetFilter: (filter: BeautyParamsDraft) => {
        set(state => ({
            streamingSettingInfo: {
                ...state.streamingSettingInfo,
                filter_settings: filter
            }
        }));
    },
    updateBaseInfo: async (
        target?: keyof SettingInfoType,
        value?: SettingInfoType[keyof SettingInfoType]
    ) => {
        const get = useStreamingStore.getState;
        const { streamingSettingInfo, setStreamingSettingInfo } = get();

        const params: UpdateSettingInfoType = {
            label_ids: streamingSettingInfo.labels.map(label => label.key) || [],
            title: streamingSettingInfo.title,
            type: streamingSettingInfo.type,
            diamond: streamingSettingInfo.diamond,
            filter_settings: streamingSettingInfo.filter_settings,
            filter_status: streamingSettingInfo.filter_status
        };

        if (target !== undefined && value !== undefined) {
            Object.assign(params, { [target]: value });
        }

        const res = await updateBaseSetting(params);
        if (res.status === 200) {
            setStreamingSettingInfo(res.data);
        }
    },
    streamingInfo: {
        live_id: '',
        user_id: '',
        heat: 0,
        cover: '',
        type: 1,
        diamond: 0,
        push_url: '',
        pull_url: { flv: "", m3u8: "" },
        chat_token: '',
        isPlaying: false,
        status: 1
    },
    setStreamingInfo: (info: Partial<StreamingInfo>) => {
        set((state) => ({
            streamingInfo: {
                ...state.streamingInfo,
                ...info
            }
        }));
    },
    resetStreamingInfo: () => {
        set(() => ({
            streamingInfo: {
                live_id: '',
                user_id: '',
                heat: 0,
                cover: '',
                type: 1,
                diamond: 0,
                push_url: '',
                pull_url: { flv: "", m3u8: "" },
                chat_token: '',
                isPlaying: false,
                status: 1
            }
        }));
    },
    streamerInfo: {
        userId: "",
        nickname: "",
        avatar: "",
        isFollowing: false,
        account: ""
    },
    setStreamerInfo: (info: Partial<StreamerInfo>) => {
        set((state) => ({
            streamerInfo: {
                ...state.streamerInfo,
                ...info
            }
        }));
    },
    resetStreamerInfo: () => {
        set(() => ({
            streamerInfo: {
                userId: "",
                nickname: "",
                avatar: "",
                isFollowing: false,
                account: ""
            }
        }));
    },
    tempFilter: initalFilter,
    setTempFilter: (updater: (draft: BeautyParamsDraft) => void) =>
        set(state => {
            return {
                tempFilter: produce(state.tempFilter, updater)
            };
        }),
    chatMessages: [],
    setChatMessages: (newMessage: ChatMessage) => {
        set((state) => ({
            chatMessages: [
                ...state.chatMessages,
                newMessage
            ]
        }));
    },
    resetChatMessages: () => {
        set(() => ({
            chatMessages: []
        }));
    },
    receivedGiftMsg: [],
    setReceivedGiftMsg: (action: "push" | "unshift", msg?: GiftMessageWithType) => {
        set((state) => {
            if (action === "push" && msg) {
                return {
                    receivedGiftMsg: [
                        ...state.receivedGiftMsg,
                        msg
                    ]
                }
            }
            return {
                receivedGiftMsg: state.receivedGiftMsg.filter((_item, i) => i !== 0)
            }
        });
    },
    resetReceivedGiftMsg: () => {
        set(() => ({
            receivedGiftMsg: []
        }));
    },
    receivedCommand: new Set<string>(),
    setReceivedCommand: (commandId: string) => {
        set((state) => {
            const commandSet = new Set<string>(state.receivedCommand);
            commandSet.add(commandId);
            return { receivedCommand: commandSet };
        });
    },
    resetReceivedCommand: () => {
        set(() => {
            return { receivedCommand: new Set() };
        });
    },
    usersInRoom: new Set<string>(),
    setUsersInRoom: (userId: string) => {
        set((state) => {
            state.usersInRoom.add(userId);
            return { usersInRoom: state.usersInRoom };
        });
    },
    resetUsersInRoom: () => {
        set(() => {
            return { usersInRoom: new Set() };
        });
    },
    announcement: { announcement: "", pinned: AnnouncementPinnedState.False },
    setAnnouncement: (announcement: Partial<Announcement>) => {
        set(state => ({
            announcement: {
                ...state.announcement,
                ...announcement
            }
        }));
    },
    roomChangeState: RoomChangeState.Init,
    setRoomChangeState: (state: RoomChangeState) => {
        set(() => ({
            roomChangeState: state
        }))
    },
    totalIncome: 0,
    setTotalIncome: (diamond: number) => {
        set(() => ({
            totalIncome: diamond
        }))
    }
});

const createStreamingStore = () => {
    useStreamingStore = initStore<BaseState>(initialState);
};

export { createStreamingStore, useStreamingStore };
